import type { FC, ReactNode } from "react";

import {
  Badge,
  Box,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  IconBox,
  IconButton,
  Row,
  Spinner,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import { RouterLink } from "src/router";
import { lineageResourcePath } from "src/utils/lineage-resource-path";

import { getDisplayNameForResource } from "./resource-display";
import {
  CONNECTOR_ELBOW_WIDTH_PX,
  connectorLeftPx,
  rowIndentPx,
} from "./usage-tree-geometry";
import type { UsageRow } from "./usage-tree-state";

type Props = {
  row: UsageRow;
  childrenId: string;
  isLastSibling: boolean;
  ancestorConnectorContinuations: readonly boolean[];
  isLoadingDisabled: boolean;
  onToggle: (row: UsageRow) => void;
};

export const UsageTreeRow: FC<Props> = ({
  row,
  childrenId,
  isLastSibling,
  ancestorConnectorContinuations,
  isLoadingDisabled,
  onToggle,
}) => {
  const display = getDisplayNameForResource(row.node.resourceType);
  const { icon: Icon } = display;
  const isExpandable = row.directDependentCount > 0;
  // exists: false means an edge points at a resource that is gone
  // We show this as a muted row to indicate that the resource is gone.
  const isMissing = !row.node.exists;
  const label = isMissing
    ? `${display.label} (missing)`
    : (row.node.name ?? display.label);
  const href = lineageResourcePath(row.node);

  const ancestorConnectorPositions = ancestorConnectorContinuations.flatMap(
    (shouldContinue, depth) => (shouldContinue ? [connectorLeftPx(depth)] : []),
  );

  return (
    <Row
      className="group"
      align="center"
      gap={2}
      px={2}
      py={1.5}
      pl={`${rowIndentPx(row.depth)}px`}
      position="relative"
      _hover={{ bg: "base.lightBackground" }}
    >
      {ancestorConnectorPositions.map((left) => (
        <Box
          key={left}
          aria-hidden
          position="absolute"
          top={0}
          bottom={0}
          left={`${left}px`}
          borderLeftWidth="2px"
          borderColor="base.border"
          pointerEvents="none"
        />
      ))}

      {row.depth > 0 && (
        <>
          <Box
            aria-hidden
            position="absolute"
            top={0}
            height={isLastSibling ? "50%" : "100%"}
            left={`${connectorLeftPx(row.depth - 1)}px`}
            borderLeftWidth="2px"
            borderColor="base.border"
            pointerEvents="none"
          />
          <Box
            aria-hidden
            position="absolute"
            top="50%"
            left={`${connectorLeftPx(row.depth - 1)}px`}
            width={`${CONNECTOR_ELBOW_WIDTH_PX}px`}
            borderTopWidth="2px"
            borderColor="base.border"
            pointerEvents="none"
          />
        </>
      )}

      {/* The chevron is a button and a button inside an anchor is invalid, so it
          sits beside the link rather than inside it. */}
      {isExpandable ? (
        <IconButton
          size="sm"
          variant="tertiary"
          aria-label={
            row.isExpanded ? "Collapse dependents" : "Expand dependents"
          }
          aria-expanded={row.isExpanded}
          aria-controls={childrenId}
          aria-busy={row.isLoadingChildren}
          icon={row.isExpanded ? ChevronDownIcon : ChevronRightIcon}
          isDisabled={isLoadingDisabled && !row.childrenLoaded}
          _expanded={{ boxShadow: "none" }}
          onClick={() => onToggle(row)}
          onMouseUp={(event) => event.currentTarget.blur()}
        />
      ) : (
        <Box boxSize={6} flexShrink={0} />
      )}

      <RowLink href={href} label={row.node.name ?? display.label}>
        <IconBox
          size="sm"
          color={display.color}
          icon={<Icon />}
          aria-hidden
          display="flex"
        />

        <TextWithTooltip color={isMissing ? "text.secondary" : "text.primary"}>
          {label}
        </TextWithTooltip>

        {row.directDependentCount > 0 && (
          <Badge size="sm">{row.directDependentCount.toLocaleString()}</Badge>
        )}

        <Box flex={1} />

        {row.isLoadingChildren && (
          <Box flexShrink={0}>
            <Spinner size="sm" />
          </Box>
        )}

        <Text size="sm" color="text.tertiary">
          {display.label}
        </Text>

        {href ? (
          <Box
            as={ExternalLinkIcon}
            boxSize={4}
            flexShrink={0}
            color="text.secondary"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            _groupFocusWithin={{ opacity: 1 }}
          />
        ) : (
          // Rows without links still need to keep the space the link icon takes up
          <Box boxSize={4} flexShrink={0} aria-hidden />
        )}
      </RowLink>
    </Row>
  );
};

type RowLinkProps = {
  href: string | null;
  label: string;
  children: ReactNode;
};

/**
 * Everything the row shows apart from the chevron is one link. A resource that
 * lineage has no page for keeps the same layout without one.
 */
const RowLink: FC<RowLinkProps> = ({ href, label, children }) => {
  if (href === null) {
    return (
      <Row align="center" gap={2} flex={1} minWidth={0}>
        {children}
      </Row>
    );
  }

  return (
    <Row
      as={RouterLink}
      to={href}
      target="_blank"
      rel="noreferrer"
      aria-label={`Open ${label} in a new tab`}
      align="center"
      gap={2}
      flex={1}
      minWidth={0}
    >
      {children}
    </Row>
  );
};
